/**
 * LiveNow — what is known about this event at this minute, as opposed to
 * what was estimated days ago: the game clock, other drivers' exit reports,
 * how busy the venue is, and what the ticket market says.
 *
 * Every line states what it is, in words (docs/10 principle 3). Resale
 * listings are called resale listings, never "tickets sold"; venue busyness
 * is "compared with its usual peak", never a head count. A line with no
 * reading is not drawn, and with no readings the whole block is absent.
 */
import { Radio, Megaphone, Activity, Ticket, DoorOpen } from 'lucide-react';
import type { Opp } from './api.js';
import { fmtDuration } from './format.js';

export function hasLiveNow(opp: Opp): boolean {
  return !!(opp.live || opp.exit_reports || opp.busyness || opp.tickets);
}

function agoWords(iso: string, now: number): string {
  const min = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60_000));
  return min < 1 ? 'just now' : `${min} min ago`;
}

export function busynessWords(b: NonNullable<Opp['busyness']>): string {
  if (b.live >= 130) return `Venue is packed — ${b.live}% of its usual peak`;
  if (b.live >= 85) return `Venue is busy — ${b.live}% of its usual peak`;
  if (b.live >= 40) return `Venue is filling — ${b.live}% of its usual peak`;
  return `Venue is quiet — ${b.live}% of its usual peak`;
}

export function ticketWords(t: NonNullable<Opp['tickets']>): string | null {
  const resale = t.resale_left === null ? null
    : t.resale_left === 0 ? 'no resale tickets listed'
      : `${t.resale_left.toLocaleString()} resale ${t.resale_left === 1 ? 'ticket' : 'tickets'} still listed`;
  const sales = t.sales === 'sold_out' ? 'Sold out at the box office'
    : t.sales === 'few_left' ? 'Few tickets left at the box office'
      : t.sales === 'available' ? 'Tickets still on sale' : null;
  if (sales && resale) return `${sales} · ${resale}`;
  if (sales) return sales;
  return resale ? resale.charAt(0).toUpperCase() + resale.slice(1) : null;
}

export function LiveTag() {
  return <span className="live-tag"><i aria-hidden="true" />Live</span>;
}

export function LiveNow({ opp, now, compact }: { opp: Opp; now: number; compact?: boolean }) {
  if (!hasLiveNow(opp)) return null;
  const { live, exit_reports: reports, busyness, tickets } = opp;
  const ticketLine = tickets ? ticketWords(tickets) : null;
  return (
    <ul className={`now${compact ? ' compact' : ''}`} aria-label="Right now">
      {live?.state === 'in' && (
        <li className="on">
          <Radio size={18} aria-hidden="true" />
          <span>
            <b><LiveTag /> {live.detail}</b>
            {live.ends_in_min !== null && (
              <span className="l2 num">About {fmtDuration(live.ends_in_min * 60_000)} to the final whistle</span>
            )}
            {live.lopsided && <span className="l2 warn">One-sided game — some will leave early</span>}
          </span>
        </li>
      )}
      {live?.state === 'post' && (
        <li className="on">
          <DoorOpen size={18} aria-hidden="true" />
          <span><b>Game over — the crowd is coming out</b></span>
        </li>
      )}
      {reports && (
        <li className="on">
          <Megaphone size={18} aria-hidden="true" />
          <span>
            <b>{reports.count === 1 ? 'A driver says' : `${reports.count} drivers say`} the crowd is coming out</b>
            <span className="l2 num">Latest report {agoWords(reports.last_at, now)}</span>
          </span>
        </li>
      )}
      {busyness && (
        <li>
          <Activity size={18} aria-hidden="true" />
          <span>
            <span>{busynessWords(busyness)}</span>
            {!compact && busyness.usual !== null && (
              <span className="l2 num">It is usually at {busyness.usual}% at this hour</span>
            )}
          </span>
        </li>
      )}
      {ticketLine && (
        <li>
          <Ticket size={18} aria-hidden="true" />
          <span>
            <span>{ticketLine}</span>
            {!compact && tickets?.resale_left !== null && (
              <span className="l2">Fewer listings usually means a fuller house</span>
            )}
          </span>
        </li>
      )}
    </ul>
  );
}
